import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { cleanDuplicates } from '../../../utils/data';
import { orderByPeriods } from '../../../utils/periodOrder';

export interface Company {
    CodEmpresa: string;
    Descripcion: string;
}
export interface Projects {
    CodProyecto: string;
    Descripcion: string;
}
export interface Dimension {
    CodProyectoVector: string;
    Descripcion: string;
}
export interface Period {
    AnoMes: string;
    CodPeriodoContable: string;
    Descripcion: string;
}
export interface Status {
    Estado: string;
}
export interface Origin {
    Origen: string;
}
export interface Provider {
    CodProveedor: string;
    Descripcion: string;
    EMail: string;
    Abreviatura: string;
    RUC: string;
    CostoPEN: number;
    CostoUSD: number;
}
export interface ProviderDetail {
    CodEmpresa: string;
    CodProyecto: string;
    AnoMes: string;
    Estado: string;
    Origen: string;
    CodProveedor: string;
    Dimension1: string;
    Dimension2: string;
    Dimension3: string;
    Dimension4: string;
    Dimension5: string;
    NumeroDocumento: string;
    CostoPEN: number;
    CostoUSD: number;
    FechaActualizacion: string;
    UsuarioActualizacion: string;
    Descripcion: string;
    FechaDocumento: string;
    SimboloMoneda: string;
}

export interface PeriodDelayData {
    CodRetraso: string
    Descripcion: string
    CostoPEN: number
    CostoUSD: number
}

export interface PeriodDelayDetailsData {
    id: string
    NombreSemana?: string
    NombreMesCorto?: string
    Anio?: string
    CostoPEN: number
    CostoUSD: number
    CostoPEN_Acumulado: number
    CostoUSD_Acumulado: number
}

export interface PeriodDelayProvidersData {
    CodProveedor: string
    Descripcion: string
    CostoPEN: number
    CostoUSD: number
}

export interface PeriodDelayProviderDetailsData {
    id: string
    NombreSemana?: string
    NombreMesCorto?: string
    Anio?: string
    CostoPEN: number
    CostoUSD: number
    CostoPEN_Acumulado: number
    CostoUSD_Acumulado: number
}

export interface ProviderSlice {
    companies: Company[],
    projects: Projects[],
    dimensions: Dimension[],
    periods: Period[],
    status: Status[],
    origins: Origin[],
    providers: Provider[],
    providersRequestParts: number[],
    providersRequesLoading: boolean,
    providerDetails: ProviderDetail[],
    providerDetailsRequestParts: number[],
    providerDetailssRequesLoading: boolean,
    periodsDelay: PeriodDelayData[],
    periodsDelayRequestParts: number[],
    periodsDelaysRequesLoading: boolean,
    periodsDelayDetails: PeriodDelayDetailsData[],
    periodsDelayDetailsRequestParts: any[],
    periodsDelayDetailsRequesLoading: boolean,
    periodsDelayProviders: PeriodDelayProvidersData[],
    periodsDelayProvidersRequestParts: any[],
    periodsDelayProvidersRequesLoading: boolean,
    periodsDelayProviderDetails: PeriodDelayProviderDetailsData[],
    periodsDelayProviderDetailsRequestParts: any[],
    periodsDelayProviderDetailsRequesLoading: boolean,
}

const initialState: ProviderSlice = {
    companies: [],
    projects: [],
    dimensions: [],
    periods: [],
    status: [],
    origins: [],
    providers: [],
    providersRequestParts: [],
    providersRequesLoading: false,
    providerDetails: [],
    providerDetailsRequestParts: [],
    providerDetailssRequesLoading: false,
    periodsDelay: [],
    periodsDelayRequestParts: [],
    periodsDelaysRequesLoading: false,
    periodsDelayDetails: [],
    periodsDelayDetailsRequestParts: [],
    periodsDelayDetailsRequesLoading: false,
    periodsDelayProviders: [],
    periodsDelayProvidersRequestParts: [],
    periodsDelayProvidersRequesLoading: false,
    periodsDelayProviderDetails: [],
    periodsDelayProviderDetailsRequestParts: [],
    periodsDelayProviderDetailsRequesLoading: false,
}

export const providerSlice = createSlice({
    name: 'provider',
    initialState,
    reducers: {
        setCompaniesData: (state, {payload}: PayloadAction<any>) => {
            state.companies = payload.data;
        },
        projectsReset: (state) => {
            state.projects = [];
        },
        setProjectsData: (state, {payload}: PayloadAction<any>) => {
            state.projects = cleanDuplicates([
                ...payload.data,
                ...state.projects,
            ], 'CodProyecto') ;
        },
        dimensionsReset: (state) => {
            state.dimensions = [];
        },
        setDimensionsData: (state, {payload}: PayloadAction<any>) => {
            state.dimensions = cleanDuplicates([
                ...payload.data,
                ...state.dimensions,
            ], 'CodProyectoVector') ;
        },
        periodsReset: (state) => {
            state.periods = [];
        },
        setPeriodsData: (state, {payload}: PayloadAction<any>) => {
            state.periods = cleanDuplicates([
                ...payload.data,
                ...state.periods,
            ], ['AnoMes', 'CodPeriodoContable']);
        },
        statusReset: (state) => {
            state.status = [];
        },
        setStatusData: (state, {payload}: PayloadAction<any>) => {
            state.status = payload.data;
        },
        originsReset: (state) => {
            state.origins = [];
        },
        setOriginData: (state, {payload}: PayloadAction<any>) => {
            state.origins = payload.data;
        },
        providersReset: (state) => {
            state.providers = [];
            state.providersRequestParts = [];
            state.providersRequesLoading = false;
        },
        setProvidersData: (state, {payload}: PayloadAction<any>) => {
            if (state.providersRequestParts.includes(payload.part)) {
                return;
            }
            state.providersRequestParts.push(payload.part);
            state.providersRequesLoading = state.providersRequestParts.length !== payload.count;

            state.providers = cleanDuplicates([
                ...payload.data,
                ...state.providers,
            ], 'CodProveedor').sort((a, b) => b.CostoPEN - a.CostoPEN);
        },
        providerDetailsReset: (state) => {
            state.providerDetails = [];
            state.providerDetailsRequestParts = [];
            state.providerDetailssRequesLoading = false;
        },
        setProviderDetailsData: (state, {payload}: PayloadAction<any>) => {
            if (state.providerDetailsRequestParts.includes(payload.part)) {
                return;
            }
            state.providerDetailsRequestParts.push(payload.part);
            state.providerDetailssRequesLoading = state.providerDetailsRequestParts.length !== payload.count;

            state.providerDetails = cleanDuplicates([
                ...payload.data,
                ...state.providerDetails,
            ], 'NumeroDocumento');
        },
        periodsDelayReset: (state) => {
            state.periodsDelay = [];
            state.periodsDelayRequestParts = [];
            state.periodsDelaysRequesLoading = false;
        },
        setPeriodsDelayData: (state, {payload}: PayloadAction<any>) => {
            if (state.periodsDelayRequestParts.includes(payload.part)) {
                return;
            }
            state.periodsDelayRequestParts.push(payload.part);
            state.periodsDelaysRequesLoading = state.periodsDelayRequestParts.length !== payload.count;

            state.periodsDelay = cleanDuplicates([
                ...payload.data,
                ...state.periodsDelay,
            ], 'CodRetraso');
        },
        periodsDelayDetailsReset: (state) => {
            state.periodsDelayDetails = [];
            state.periodsDelayDetailsRequestParts = [];
            state.periodsDelayDetailsRequesLoading = false;
        },
        setPeriodsDelayDetailsData: (state, {payload}: PayloadAction<any>) => {
            if (state.periodsDelayDetailsRequestParts.includes(payload.part)) {
                return;
            }
            state.periodsDelayDetailsRequestParts.push(payload.part);
            state.periodsDelayDetailsRequesLoading = state.periodsDelayDetailsRequestParts.length !== payload.count;

            state.periodsDelayDetails = orderByPeriods(cleanDuplicates([
                ...payload.data.map((i: any) => ({...i, id: i.NombreMesCorto || i.NombreSemana || i.Anio || i.Periodo})),
                ...state.periodsDelayDetails,
            ], 'id'), 'id', true);
        },
        periodsDelayProvidersReset: (state) => {
            state.periodsDelayProviders = [];
            state.periodsDelayProvidersRequestParts = [];
            state.periodsDelayProvidersRequesLoading = false;
        },
        setPeriodsDelayProvidersData: (state, {payload}: PayloadAction<any>) => {
            if (state.periodsDelayProvidersRequestParts.includes(payload.part)) {
                return;
            }
            state.periodsDelayProvidersRequestParts.push(payload.part);
            state.periodsDelayProvidersRequesLoading = state.periodsDelayProvidersRequestParts.length !== payload.count;

            state.periodsDelayProviders = cleanDuplicates([
                ...payload.data,
                ...state.periodsDelayProviders,
            ], 'CodProveedor').sort((a, b) => Math.abs(b.CostoPEN) - Math.abs(a.CostoPEN));
        },
        periodsDelayProviderDetailsReset: (state) => {
            state.periodsDelayProviderDetails = [];
            state.periodsDelayProviderDetailsRequestParts = [];
            state.periodsDelayProviderDetailsRequesLoading = false;
        },
        setPeriodsDelayProviderDetailsData: (state, {payload}: PayloadAction<any>) => {
            if (state.periodsDelayProviderDetailsRequestParts.includes(payload.part)) {
                return;
            }
            state.periodsDelayProviderDetailsRequestParts.push(payload.part);
            state.periodsDelayProviderDetailsRequesLoading = state.periodsDelayProviderDetailsRequestParts.length !== payload.count;

            state.periodsDelayProviderDetails = orderByPeriods(cleanDuplicates([
                ...payload.data.map((i: any) => ({...i, id: i.NombreMesCorto || i.NombreSemana || i.Anio || i.Periodo})),
                ...state.periodsDelayProviderDetails,
            ], 'id'), 'id', true);
        },
    }
})


export const {
    setCompaniesData,
    projectsReset,
    setProjectsData,
    dimensionsReset,
    setDimensionsData,
    periodsReset,
    setPeriodsData,
    statusReset,
    setStatusData,
    originsReset,
    setOriginData,
    providersReset,
    setProvidersData,
    providerDetailsReset,
    setProviderDetailsData,
    periodsDelayReset,
    setPeriodsDelayData,
    periodsDelayDetailsReset,
    setPeriodsDelayDetailsData,
    periodsDelayProvidersReset,
    setPeriodsDelayProvidersData,
    periodsDelayProviderDetailsReset,
    setPeriodsDelayProviderDetailsData,
} = providerSlice.actions
